import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  GridPagination,
  useGridApiRef,
} from "@mui/x-data-grid";
import type { GridColDef } from "@mui/x-data-grid";
import type { Timestamp } from "firebase/firestore";
import { useEffect, useState } from "react";
import type { Promocion } from "../../firebase/firestore/promociones";
import { generateGradientFromColor } from "../../firebase/firestore/promociones";
import { usePromocionStore } from "../../zustand/usePromocionesStore";
import ModalDeletePromocion from "../modals/ModalDeletePromocion";
import ModalFormPromocion from "../modals/ModalFormPromocion";
import DataTable from "./DataTable";

import dayjs from "dayjs";
import "dayjs/locale/es";

dayjs.locale("es");

const formatFechaCompleta = (timestamp: Timestamp | null | undefined): string => {
  if (!timestamp) return "-";
  try {
    const fecha = dayjs(timestamp.toDate());
    if (!fecha.isValid()) return "-";
    const dia = fecha.format("D");
    const mes = fecha.format("MMMM");
    const hora = fecha.format("h:mma").toLowerCase();
    return `${dia} de ${mes} a las ${hora}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
};

export default function TablePromociones() {
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState<"activo" | "inactivo" | "todos">("todos");
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);
  const [entity, setEntity] = useState<Promocion>();
  const [promocionToDelete, setPromocionToDelete] = useState<Promocion>();
  const { allPromociones, fetchAllPromociones } = usePromocionStore();

  useEffect(() => {
    fetchAllPromociones();
  }, [fetchAllPromociones]);

  const filteredRows = allPromociones.filter((row) => {
    // Filtrar por estado
    if (estadoFilter !== "todos" && row.estado !== estadoFilter) {
      return false;
    }

    if (!search.trim()) return true;

    const searchLower = search.toLowerCase();

    // Search in titulo
    if (row.titulo?.toLowerCase().includes(searchLower)) return true;

    // Search in descripcion
    if (row.descripcion?.toLowerCase().includes(searchLower)) return true;

    // Search in estado
    if (row.estado?.toLowerCase().includes(searchLower)) return true;

    return false;
  });

  const handleCloseModal = () => {
    setIsOpenModal(false);
    setEntity(undefined);
  };

  const handleCloseDeleteModal = () => {
    setIsOpenModalDelete(false);
    setPromocionToDelete(undefined);
  };

  const handleCreate = () => {
    setEntity(undefined);
    setIsOpenModal(true);
  };

  const handleEdit = (promocion: Promocion) => {
    setEntity(promocion);
    setIsOpenModal(true);
  };

  const handleDelete = (promocion: Promocion) => {
    setPromocionToDelete(promocion);
    setIsOpenModalDelete(true);
  };

  const columns: GridColDef[] = [
    {
      field: "acciones",
      headerName: "Acciones",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            gap: 1,
          }}
        >
          <Tooltip title="Editar">
            <Button
              variant="outlined"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => handleEdit(params.row)}
            >
              <Edit sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
          <Tooltip title="Eliminar">
            <Button
              variant="outlined"
              color="error"
              sx={{ minWidth: "auto", padding: "5px" }}
              onClick={() => handleDelete(params.row)}
            >
              <Delete sx={{ fontSize: 20 }} />
            </Button>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "titulo",
      headerName: "Título",
      width: 200,
      sortable: true,
    },
    {
      field: "descripcion",
      headerName: "Descripción",
      width: 300,
      sortable: true,
      renderCell: (params) => (
        <Box
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 300,
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 150,
      sortable: true,
      renderCell: (params) => {
        const estado = params.value as "activo" | "inactivo";
        const isActivo = estado === "activo";
        return (
          <Chip
            label={estado.charAt(0).toUpperCase() + estado.slice(1)}
            sx={{
              backgroundColor: isActivo ? "#4caf50" : "#9e9e9e",
              color: "#fff",
              fontWeight: "bold",
            }}
          />
        );
      },
    },
    {
      field: "color",
      headerName: "Color",
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const color = params.value as { color1: string; color2: string } | string | undefined;
        let gradient: { color1: string; color2: string };
        
        if (color && typeof color === 'object' && 'color1' in color && 'color2' in color) {
          // Usar el objeto color guardado en Firebase
          gradient = color;
        } else {
          // Compatibilidad con formato antiguo o generar desde string
          gradient = generateGradientFromColor(color as string);
        }
        
        return (
          <Box
            sx={{
              width: "100%",
              height: 40,
              borderRadius: 1,
              background: `linear-gradient(180deg, ${gradient.color1}, ${gradient.color2})`,
              border: "1px solid #ddd",
            }}
          />
        );
      },
    },
    {
      field: "fechaCreacion",
      headerName: "Fecha de creación",
      width: 220,
      sortable: true,
      valueGetter: (_value, row) => {
        return row.createdAt ? row.createdAt.toMillis() : 0;
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderCell: (params) => {
        return formatFechaCompleta(params.row.createdAt);
      },
    },
    {
      field: "ultimaActualizacion",
      headerName: "Última actualización",
      width: 220,
      sortable: true,
      valueGetter: (_value, row) => {
        return row.updatedAt ? row.updatedAt.toMillis() : 0;
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderCell: (params) => {
        return formatFechaCompleta(params.row.updatedAt);
      },
    },
  ];

  return (
    <>
      <ModalFormPromocion
        isOpen={isOpenModal}
        onClose={handleCloseModal}
        entity={entity}
      />
      {promocionToDelete && (
        <ModalDeletePromocion
          isOpen={isOpenModalDelete}
          onClose={handleCloseDeleteModal}
          promocion={promocionToDelete}
        />
      )}
      <Paper sx={{ padding: 2, width: "auto", marginTop: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "space-between",
            marginBottom: 2,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            label="Buscar promociones"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 200, maxWidth: 400 }}
          />
          <Select
            value={estadoFilter}
            onChange={(e) =>
              setEstadoFilter(
                e.target.value as "activo" | "inactivo" | "todos"
              )
            }
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="todos">Todos los estados</MenuItem>
            <MenuItem value="activo">Activo</MenuItem>
            <MenuItem value="inactivo">Inactivo</MenuItem>
          </Select>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            size="small"
          >
            Crear promoción
          </Button>
        </Box>

        <DataTable<Promocion>
          apiRef={apiRef}
          rows={filteredRows.slice(
            paginationModel.page * paginationModel.pageSize,
            (paginationModel.page + 1) * paginationModel.pageSize
          )}
          columns={columns}
          paginationModel={paginationModel}
          checkboxSelection={false}
          onSelectionModelChange={() => {}}
          onRowDoubleClick={(params) => {
            handleEdit(params.row);
          }}
          slots={{
            footer: () => <GridPagination />,
          }}
          onPaginationModelChange={(newModel: {
            page: number;
            pageSize: number;
          }) => {
            setPaginationModel(newModel);
          }}
          rowCount={filteredRows.length}
          sortingMode="client"
        />
      </Paper>
    </>
  );
}
